import React, { useEffect, useContext, useState } from "react"
import { useAppContext } from "../../helpers/appContext"
import SbEditable from "storyblok-react"
import PropTypes from "prop-types"
import linkHandler from "../../helpers/storyblokLinkHandler"
import Anchor from "./anchor"
import YouVisit from "../you-visit"
import VideoOverlay from "../video-overlay"
import CalendlyOverlay from "../calendly-overlay"
import ButtonAction from "./button"
import ModalFormV2 from "../modal-form-v2"
import ModalFormV2Split from "../modal-form-v2-split"

const ButtonV2 = ({
    blok,
    className,
    label,
    accessibilityLabel,
    icon,
    link,
    linkBehavior,
    variant,
    spacing,
    full_width,
    onClick,
    disabled,
    loading,
}) => {
    // Icon Mappings
    let iconType
    function getFaIcon(iconValue) {
        switch (iconValue) {
            case "Facebook":
                iconType = "fab"
                return "facebook-f"
            case "Youtube":
                iconType = "fab"
                return "youtube"
            case "Instagram":
                iconType = "fab"
                return "instagram"
            case "Linkedin":
                iconType = "fab"
                return "linkedin-in"
            case "Edit2":
                return "pencil"
            case "Eye":
                return "eye"
            case "Check":
                return "check"
            case "X":
                return "times"
            case "ChevronUp":
                return "chevron-up"
            case "ChevronDown":
                return "chevron-down"
            case "ChevronLeft":
                return "chevron-left"
            case "ChevronRight":
                return "chevron-right"
            case "ArrowUp":
                return "arrow-up"
            case "ArrowDown":
                return "arrow-down"
            case "ArrowLeft":
                return "arrow-left"
            case "ArrowRight":
                return "arrow-right"
            case "File":
                return "file"
            case "Folder":
                return "folder"
            case "Info":
                return "info"
            case "Lock":
                return "lock"
            case "MapPin":
                return "map-pin"
            case "Meh":
                return "meh"
            case "Frown":
                return "frown"
            case "Smile ":
                return "smile"
            case "BookOpen":
                return "book-open"
            case "Play":
                return "play"
            case "Download":
                return "cloud-download"
            case "Email":
                return "envelope"
            case "Mail":
                return "envelope"
            default:
                return iconValue
        }
    }

    const { state, dispatch } = useAppContext();

    if (blok !== null) {
        if (
            blok.modal_title ||
            blok.modal_description ||
            (blok.modal_brochure_image && blok.modal_brochure_image.filename) ||
            (blok.modal_form && blok.modal_form.length > 0) ||
            blok.form_title
        ) {
            function openModal() {
                dispatch({
                    type: 'MODAL',
                    payload: {
                        overlay_open: true,
                        overlay_modal_title: blok.modal_title,
                        overlay_modal_description: blok.modal_description,
                        overlay_modal_brochure_image: blok.modal_brochure_image.filename,
                        overlay_modal_form: blok.modal_form,
                        overlay_modal_form_title: blok.form_title,
                        overlay_modal_split: blok.modal_split,
                    },
                });
                document && document.body.classList.add("g--no-scroll-v2")
            }
            return (
                <SbEditable content={blok}>
                    <ButtonAction
                        dataName="form"
                        onClick={() => openModal()}
                        className={className}
                        label={blok.label}
                        accessibilityLabel={blok.accessibilityLabel}
                        icon={getFaIcon(blok.icon)}
                        iconType={iconType}
                        variant={blok.variant}
                        spacing={blok.spacing}
                        fullWidth={blok.full_width}
                    />
                </SbEditable>
            )
        }
        if (blok.you_visit_hyperlink) {
            return (
                <YouVisit
                    you_visit_hyperlink={blok.you_visit_hyperlink}
                    className={className}
                    label={blok.label}
                    accessibilityLabel={blok.accessibilityLabel}
                    icon={getFaIcon(blok.icon)}
                    variant={blok.variant}
                    spacing={blok.spacing}
                    fullWidth={blok.full_width}
                />
            )
        } else if (blok.calendly_url) {
            return (
                <CalendlyOverlay
                    calendly_url={blok.calendly_url}
                    className={className}
                    label={blok.label}
                    accessibilityLabel={blok.accessibilityLabel}
                    icon={getFaIcon(blok.icon)}
                    variant={blok.variant}
                    spacing={blok.spacing}
                    fullWidth={blok.full_width}
                />
            )
        } else if (blok.video_url) {
            useEffect(() => {
                dispatch({
                    type: 'UPDATE_CATALOG_INFO',
                    payload: {
                        Video: {
                            id: blok.video_url.slice(blok.video_url.indexOf("embed/") + "embed/".length),
                            name: "",
                            url: blok.video_url,
                        },
                    },
                });
            }, [])
            function openVideo() {
                dispatch({
                    type: 'VIDEO',
                    payload: {
                        overlay_open: true,
                        overlay_video_url: blok.video_url,
                    },
                });
                
                let updatedCatalog = { ...state.catalogInfo.Video };

                if (state.catalogInfo.Campus.id) {
                    updatedCatalog.relatedCatalogObjects = {
                        ...updatedCatalog.relatedCatalogObjects,
                        Campus: [state.catalogInfo.Campus.id],
                    };
                }
            
                if (state.catalogInfo.Degree.id) {
                    updatedCatalog.relatedCatalogObjects = {
                        ...updatedCatalog.relatedCatalogObjects,
                        Degree: [state.catalogInfo.Degree.id],
                    };
                }
        
                if (state.catalogInfo.Program.id) {
                    updatedCatalog.relatedCatalogObjects = {
                        ...updatedCatalog.relatedCatalogObjects,
                        Program: [state.catalogInfo.Program.id],
                    };
                }
        
                if (state.catalogInfo.Form.id) {
                    updatedCatalog.relatedCatalogObjects = {
                        ...updatedCatalog.relatedCatalogObjects,
                        Form: [state.catalogInfo.Form.id],
                    };
                }
        
                if (state.catalogInfo.Tags) {
                    updatedCatalog.relatedCatalogObjects = {
                        ...updatedCatalog.relatedCatalogObjects,
                        Tags: state.catalogInfo.Tags,
                    };
                }
            
                dispatch({
                    type: 'UPDATE_PAGE_INFO',
                    payload: {
                        catalog: {
                            Video: updatedCatalog,
                        },
                    },
                });

                document && document.body.classList.add("g--no-scroll-v2")
            }
            return (
                <SbEditable content={blok}>
                    <ButtonAction
                        dataName="video"
                        onClick={() => openVideo()}
                        className={className}
                        label={blok.label}
                        accessibilityLabel={blok.accessibilityLabel}
                        icon={getFaIcon(blok.icon)}
                        iconType={iconType}
                        variant={blok.variant}
                        spacing={blok.spacing}
                        fullWidth={blok.full_width}
                    />
                </SbEditable>
            )
        } else {
            return (
                <SbEditable content={blok}>
                    <Anchor
                        dataName="button"
                        className={className}
                        label={blok.label}
                        accessibilityLabel={blok.accessibilityLabel}
                        icon={getFaIcon(blok.icon)}
                        iconType={iconType}
                        link={linkHandler(blok.link)}
                        linkBehavior={blok.linkBehavior}
                        variant={blok.variant}
                        spacing={blok.spacing}
                        fullWidth={blok.full_width}
                    />
                </SbEditable>
            )
        }
    }

    if (link !== null) {
        return (
            <Anchor
                dataName="button"
                className={className}
                label={label}
                accessibilityLabel={accessibilityLabel}
                icon={getFaIcon(icon)}
                iconType={iconType}
                link={link}
                linkBehavior={linkBehavior}
                variant={variant}
                spacing={spacing}
                fullWidth={full_width}
            />
        )
    }

    return (
        <ButtonAction
            dataName="button"
            label={label}
            className={className}
            icon={getFaIcon(icon)}
            iconType={iconType}
            onClick={onClick}
            disabled={disabled}
            fullWidth={full_width}
            loading={loading}
        />
    )
}

ButtonV2.defaultProps = {
    blok: null,
    className: "",
    label: "",
    accessibilityLabel: "",
    icon: "",
    link: null,
    linkBehavior: false,
    variant: "--text",
    spacing: false,
    fullWidth: false,
    onClick: () => { },
    disabled: false,
    loading: false,
}

ButtonV2.propTypes = {
    blok: PropTypes.shape({
        label: PropTypes.string,
        accessibilityLabel: PropTypes.string,
        icon: PropTypes.string,
        link: PropTypes.shape({
            linktype: PropTypes.string,
            cached_url: PropTypes.string,
            url: PropTypes.string,
        }),
        linkBehavior: PropTypes.bool,
        variant: PropTypes.string,
    }),
    className: PropTypes.string,
    label: PropTypes.string,
    accessibilityLabel: PropTypes.string,
    icon: PropTypes.string,
    link: PropTypes.string,
    linkBehavior: PropTypes.bool,
    variant: PropTypes.string,
    spacing: PropTypes.bool,
    full_width: PropTypes.bool,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
}

export default ButtonV2
